/**
 * Stub hooks for VideoSDK features that don't have direct Agora equivalents
 * These provide no-op implementations to prevent errors during migration
 */

import { useContext, useState, useCallback, useRef, useEffect } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import AgoraContext from '../context/AgoraContext';
import { startStt, stopStt } from '../services/agoraSttService';

// useTranscription — wired to the real Agora STT start/stop endpoints
// (used to be a no-op stub that just logged a warning, which silently
// dropped transcription requests from any caller using this hook —
// most notably TopBarWebinar.js and SettingsPanelOld.jsx). It now hits
// /agora/stt/start which spawns a backend STT agent in the channel.
//
// The hook reads meetingId from the surrounding AgoraMeetingProvider
// (callers historically invoked `useTranscription()` with no args, which
// was fine when it was a no-op — for the real impl we need the channel).
//
// `transcription` array stays empty for now: live segments arrive via
// socket events in MeetingStateContext / transcriptBuffer and are
// rendered by the AI Notetaker panel directly. Wiring them through this
// hook would duplicate that flow without any caller using the data.
export function useAgoraTranscription() {
  const ctx = useContext(AgoraContext);
  const meetingId = ctx?.meetingId || null;
  const [transcriptionState, setTranscriptionState] = useState('STOPPED');
  const inFlightRef = useRef(false);

  const startTranscription = useCallback(async (config) => {
    if (!meetingId) {
      console.warn('[useAgoraTranscription] start called outside AgoraMeetingProvider — skipping');
      return null;
    }
    if (inFlightRef.current) {
      console.log('[useAgoraTranscription] start already in flight — skipping');
      return null;
    }
    inFlightRef.current = true;
    try {
      setTranscriptionState('STARTING');
      // Backend is idempotent: if an agent already exists for this channel
      // it returns { alreadyRunning: true, agentId, pubBotUid } instead of
      // creating a duplicate.
      const languages = (config && Array.isArray(config.languages) && config.languages.length)
        ? config.languages
        : ['en-US'];
      const result = await startStt(meetingId, languages);
      if (result?.success || result?.agentId) {
        setTranscriptionState('STARTED');
        console.log('[useAgoraTranscription] STT started for', meetingId, 'agent=', result.agentId, 'alreadyRunning=', !!result.alreadyRunning);
        return result;
      }
      console.warn('[useAgoraTranscription] STT start returned unsuccessful:', result);
      setTranscriptionState('STOPPED');
      return null;
    } catch (err) {
      console.error('[useAgoraTranscription] STT start failed:', err?.message || err);
      setTranscriptionState('STOPPED');
      return null;
    } finally {
      inFlightRef.current = false;
    }
  }, [meetingId]);

  const stopTranscription = useCallback(async () => {
    if (!meetingId) {
      console.warn('[useAgoraTranscription] stop called outside AgoraMeetingProvider — skipping');
      return null;
    }
    try {
      setTranscriptionState('STOPPING');
      const result = await stopStt(meetingId);
      setTranscriptionState('STOPPED');
      return result;
    } catch (err) {
      console.error('[useAgoraTranscription] STT stop failed:', err?.message || err);
      setTranscriptionState('STOPPED');
      return null;
    }
  }, [meetingId]);

  return {
    transcriptionState,
    transcription: [],
    startTranscription,
    stopTranscription,
  };
}

// useTranslation stub - Agora has different translation API
export function useAgoraTranslation() {
  return {
    translationState: null,
    translation: [],
    startTranslation: () => console.warn('Translation requires custom implementation with Agora'),
    stopTranslation: () => console.warn('Translation requires custom implementation with Agora'),
  };
}

// useWhiteboard - Lazy-loaded to avoid mathjs/Fraction.js compatibility issue
// The actual implementation is in useAgoraWhiteboard.js but we can't import it statically
export function useAgoraWhiteboard() {
  const [fastboard, setFastboard] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roomInfo, setRoomInfo] = useState(null);
  const [isWhiteboardOpen, setIsWhiteboardOpen] = useState(false);
  const containerRef = useRef(null);

  const startWhiteboard = useCallback(async (config = {}) => {
    console.log('[Whiteboard Stub] startWhiteboard called with config:', config);
    setIsLoading(true);
    setError(null);

    try {
      // Dynamically import the whiteboard module
      console.log('[Whiteboard Stub] Dynamically importing useAgoraWhiteboard...');
      const { startWhiteboardSession } = await import('./useAgoraWhiteboard');
      console.log('[Whiteboard Stub] Import successful, calling startWhiteboardSession...');

      const result = await startWhiteboardSession(config);
      console.log('[Whiteboard Stub] startWhiteboardSession result:', result ? 'success' : 'null');

      if (result) {
        console.log('[Whiteboard Stub] Creating fastboard object with room:', !!result.room);
        // Create a fastboard-like object for WhiteboardContainer compatibility
        setFastboard({
          room: result.room,
          sdk: result.sdk,
          destroy: async () => {
            if (result.room) {
              await result.room.disconnect();
            }
          },
        });
        setRoomInfo({ uuid: result.roomUUID });
        setIsWhiteboardOpen(true);
        console.log('[Whiteboard Stub] State updated: isWhiteboardOpen=true');
      } else {
        console.warn('[Whiteboard Stub] startWhiteboardSession returned null');
      }

      setIsLoading(false);
      return result;
    } catch (err) {
      console.error('[Whiteboard Stub] Failed to start whiteboard:', err);
      console.error('[Whiteboard Stub] Error details:', err.message, err.stack);
      setError(err);
      setIsLoading(false);
      return null;
    }
  }, []);

  const stopWhiteboard = useCallback(async () => {
    try {
      if (fastboard?.room) {
        await fastboard.room.disconnect();
      } else if (fastboard?.destroy) {
        await fastboard.destroy();
      }
      setFastboard(null);
      setIsWhiteboardOpen(false);
      setRoomInfo(null);
    } catch (err) {
      console.error('[Whiteboard] Error stopping whiteboard:', err);
    }
  }, [fastboard]);

  const joinWhiteboard = useCallback(async (roomUUID, config = {}) => {
    return startWhiteboard({ ...config, existingRoomUUID: roomUUID });
  }, [startWhiteboard]);

  // Bind WhiteWebSdk room to container element when both are available
  useEffect(() => {
    if (fastboard?.room && containerRef.current && isWhiteboardOpen) {
      try {
        fastboard.room.bindHtmlElement(containerRef.current);
        console.log('[Whiteboard Stub] Room bound to container element');
      } catch (err) {
        console.error('[Whiteboard Stub] Failed to bind room to container:', err);
      }
    }
    return () => {
      if (fastboard?.room) {
        try { fastboard.room.bindHtmlElement(null); } catch(e) {}
      }
    };
  }, [fastboard, isWhiteboardOpen]);

  const whiteboardUrl = isWhiteboardOpen ? `whiteboard://${roomInfo?.uuid || 'local'}` : null;

  return {
    fastboard,
    isLoading,
    error,
    roomInfo,
    isWhiteboardOpen,
    whiteboardUrl,
    startWhiteboard,
    stopWhiteboard,
    joinWhiteboard,
    containerRef,
    currentConfig: null,
  };
}

// createCameraVideoTrack equivalent for Agora
// Track the last Agora video track created for pre-join preview so it can be
// properly closed before the meeting creates its own track.  Chromium browsers
// (Chrome, Brave, Edge) exclusively lock the camera — if the preview track is
// still alive when AgoraContext.join() tries to create a new track, the second
// call fails silently and the user gets no video.  Firefox is more permissive
// which is why it works there but not on Chrome/Brave.
let _previewVideoTrack = null;
let _previewAudioTrack = null;

export function closePreviewVideoTrack() {
  if (_previewVideoTrack) {
    try {
      _previewVideoTrack.stop();
      _previewVideoTrack.close();
      console.log('[useAgoraStubs] Preview video track closed');
    } catch (e) {
      console.warn('[useAgoraStubs] Error closing preview video track:', e);
    }
    _previewVideoTrack = null;
  }
}

export function closePreviewAudioTrack() {
  if (_previewAudioTrack) {
    try {
      _previewAudioTrack.stop();
      _previewAudioTrack.close();
      console.log('[useAgoraStubs] Preview audio track closed');
    } catch (e) {
      console.warn('[useAgoraStubs] Error closing preview audio track:', e);
    }
    _previewAudioTrack = null;
  }
}

/**
 * Close all preview tracks (video + audio).
 * Call this when navigating away from pages that use camera/mic preview
 * (setup page, pre-launch check, etc.) to release hardware.
 */
export function closeAllPreviewTracks() {
  closePreviewVideoTrack();
  closePreviewAudioTrack();
}

export async function createAgoraCameraVideoTrack(config = {}) {
  const {
    cameraId,
    encoderConfig = '1080p_2',
    optimizationMode = 'motion',
  } = config;

  const agoraEncoderConfig = mapEncoderConfig(encoderConfig);

  // Close any existing preview track first to release the camera
  closePreviewVideoTrack();

  try {
    const videoTrack = await AgoraRTC.createCameraVideoTrack({
      cameraId,
      encoderConfig: agoraEncoderConfig,
      // Default to 'motion' — keeps frame rate steady under bandwidth pressure
      // (the SDK default 'detail' drops fps to preserve resolution, which on
      // shaky home Wi-Fi produced 1-2 fps streams). Only honor 'detail' if a
      // caller explicitly opts in.
      optimizationMode: 'motion',
    });

    // Store reference so we can close it before the meeting creates its own track
    _previewVideoTrack = videoTrack;

    return new MediaStream([videoTrack.getMediaStreamTrack()]);
  } catch (error) {
    // Retry once after a short delay — "can not find stream" is often a timing issue
    if (error?.message?.includes('can not find stream')) {
      console.warn('Camera track creation failed, retrying in 500ms...', error.message);
      await new Promise(r => setTimeout(r, 500));
      try {
        const videoTrack = await AgoraRTC.createCameraVideoTrack({
          cameraId,
          encoderConfig: agoraEncoderConfig,
          // Default to 'motion' — keeps frame rate steady under bandwidth pressure
      // (the SDK default 'detail' drops fps to preserve resolution, which on
      // shaky home Wi-Fi produced 1-2 fps streams). Only honor 'detail' if a
      // caller explicitly opts in.
      optimizationMode: 'motion',
        });
        _previewVideoTrack = videoTrack;
        return new MediaStream([videoTrack.getMediaStreamTrack()]);
      } catch (retryErr) {
        console.error('Failed to create camera video track on retry:', retryErr);
        return null;
      }
    }
    console.error('Failed to create camera video track:', error);
    return null;
  }
}

/**
 * Switch the camera on the existing preview video track using setDevice().
 * Agora's recommended pattern: preserves the track identity so any attached
 * processors (virtual background, AINS denoiser on audio, etc.) stay piped,
 * and avoids orphaning hardware handles from repeat track recreations.
 *
 * Returns a fresh MediaStream wrapping the track's current MediaStreamTrack
 * — same shape as createAgoraCameraVideoTrack so callers stay identical.
 *
 * If no preview track exists yet (first selection), or setDevice throws
 * (e.g. device unplugged mid-call), falls back to creating a new track.
 */
export async function switchPreviewCameraDevice(deviceId) {
  if (_previewVideoTrack && typeof _previewVideoTrack.setDevice === 'function') {
    try {
      await _previewVideoTrack.setDevice(deviceId);
      return new MediaStream([_previewVideoTrack.getMediaStreamTrack()]);
    } catch (err) {
      console.warn('[useAgoraStubs] setDevice on preview camera failed, recreating:', err);
      // Fall through to recreate
    }
  }
  return createAgoraCameraVideoTrack({ cameraId: deviceId });
}

/**
 * Switch the microphone on the existing preview audio track using setDevice().
 * See switchPreviewCameraDevice() above for rationale.
 */
export async function switchPreviewMicDevice(deviceId) {
  if (_previewAudioTrack && typeof _previewAudioTrack.setDevice === 'function') {
    try {
      await _previewAudioTrack.setDevice(deviceId);
      return new MediaStream([_previewAudioTrack.getMediaStreamTrack()]);
    } catch (err) {
      console.warn('[useAgoraStubs] setDevice on preview mic failed, recreating:', err);
      // Fall through to recreate
    }
  }
  return createAgoraMicrophoneAudioTrack({ microphoneId: deviceId });
}

// createMicrophoneAudioTrack equivalent for Agora
export async function createAgoraMicrophoneAudioTrack(config = {}) {
  const { microphoneId } = config;

  // Close any existing preview audio track first to release the mic
  closePreviewAudioTrack();

  try {
    const audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
      microphoneId,
    });

    // Store reference so we can close it when navigating away
    _previewAudioTrack = audioTrack;

    return new MediaStream([audioTrack.getMediaStreamTrack()]);
  } catch (error) {
    if (error?.message?.includes('can not find stream')) {
      console.warn('Mic track creation failed, retrying in 500ms...', error.message);
      await new Promise(r => setTimeout(r, 500));
      try {
        const audioTrack = await AgoraRTC.createMicrophoneAudioTrack({ microphoneId });
        _previewAudioTrack = audioTrack;
        return new MediaStream([audioTrack.getMediaStreamTrack()]);
      } catch (retryErr) {
        console.error('Failed to create microphone audio track on retry:', retryErr);
        return null;
      }
    }
    console.error('Failed to create microphone audio track:', error);
    return null;
  }
}

// Map VideoSDK encoder config strings to Agora format
function mapEncoderConfig(videosdkConfig) {
  const configMap = {
    'h1080p_w1920p': '1080p_2',
    'h720p_w1280p': '720p_2',
    'h540p_w960p': '540p_2',
    'h480p_w640p': '480p_2',
    'h360p_w640p': '360p_4',
    'h240p_w320p': '240p_1',
  };

  return configMap[videosdkConfig] || videosdkConfig || '1080p_2';
}

// getNetworkStats stub
export async function getAgoraNetworkStats() {
  return {
    downloadSpeed: 0,
    uploadSpeed: 0,
  };
}
